import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import { useCss, a, m } from "kremling";
import { CpLabel } from "@components";
import { useMergeRefs } from "@floating-ui/react";

type ResizeOption = "auto" | "none" | "both" | "horizontal" | "vertical";

export type CpTextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange" | "value" | "ref"
> & {
  className?: string | object;
  error?: boolean | string;
  height?: number;
  label?: string;
  maxHeight?: number;
  maxWidth?: number;
  onChange: (value: string) => void;
  resize?: ResizeOption;
  style?: React.CSSProperties;
  width?: number;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  message?: string;
  value: string;
};

export const CpTextarea = forwardRef<HTMLTextAreaElement, CpTextareaProps>(
  function CpTextarea(props, ref) {
    const {
      className,
      error,
      height,
      label,
      maxHeight,
      maxWidth,
      onChange,
      resize = "auto",
      style,
      width,
      containerProps = {},
      ...rest
    } = props;
    const message =
      typeof props.error === "string" ? props.error : props.message;
    const { className: containerClassName, ...containerPropsRest } =
      containerProps;

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = useMergeRefs([ref, textareaRef]);

    const scope = useCss(css);

    const adjustHeight = useCallback(() => {
      if (!textareaRef.current) return;
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }, []);

    useEffect(() => {
      if (resize !== "auto") return;
      adjustHeight();
    }, [props.value, resize, adjustHeight]);

    return (
      <div
        {...scope}
        className={a(containerClassName, m("cp-textarea--error", error))}
        {...containerPropsRest}
      >
        {label && (
          <CpLabel htmlFor={rest?.id} required={rest?.required} error={!!error}>
            {label}
          </CpLabel>
        )}
        <div>
          <textarea
            className={a("cp-textarea").a(className)}
            rows={1}
            {...rest}
            style={{
              resize: resize !== "auto" ? resize : "none",
              minHeight: height || "100%",
              minWidth: width || "4rem",
              maxHeight: resize && maxHeight,
              maxWidth: resize && maxWidth,
              ...style,
            }}
            ref={mergedRef}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              onChange(e.target.value);
            }}
          />
          {message && <div className="cp-textarea-message">{message}</div>}
        </div>
      </div>
    );
  },
);

// language=css
const css = `
  .cp-textarea {
    box-sizing: border-box;
    width: 100%;
    border: 1px solid var(--cps-color-border);
    border-radius: .4rem;
    padding: .4rem 1.2rem;
    transition: box-shadow var(--cp-form-transition-duration) ease;
    background-color: var(--cps-color-light-well);
  }
  .cp-textarea:focus-visible,
  .cp-textarea:focus-within {
    border-color: var(--cps-color-primary);
    box-shadow: var(--cp-form-focus-state);
    outline: none;
  }
  .cp-textarea:focus-visible .cp-icon,
  .cp-textarea:focus-within .cp-icon {
    color: var(--cps-color-primary);
  }

  .cp-textarea--error textarea {
    border-color: var(--cp-color-input-error-border);
  }

  .cp-textarea--error textarea:focus {
    box-shadow: var(--cp-form-focus-state-error);
  }
  .cp-textarea--error .cp-textarea-message {
    color: var(--cp-color-app-error-text);
  }

  .cp-textarea-message {
    color: var(--cp-color-app-secondary-text);
    font-size: 1.2rem;
    margin-top: .4rem;
  }
`;
